import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function Documents() {
  const [view, setView] = useState('offer'); // offer | contracts
  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  return (
    <Layout>
      <div style={styles.header}>
        <h1 style={styles.title}>Оферта и договоры</h1>
      </div>

      <div style={styles.tabs}>
        <div style={{ ...styles.tab, ...(view === 'offer' ? styles.tabActive : {}) }} onClick={() => setView('offer')}>Оферта</div>
        <div style={{ ...styles.tab, ...(view === 'contracts' ? styles.tabActive : {}) }} onClick={() => setView('contracts')}>Договоры</div>
      </div>

      {view === 'offer' ? <OfferTab headers={headers} /> : <ContractsTab headers={headers} />}
    </Layout>
  );
}

// ── Вкладка «Оферта» ──
function OfferTab({ headers }) {
  const [versions, setVersions] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/admin/offer-versions`, { headers }).then(r => setVersions(r.data));
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (!title.trim() || !content.trim()) { setErr('Заполните заголовок и текст'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/admin/offer-versions`, { title, content }, { headers });
      setShowNew(false); setTitle(''); setContent('');
      load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось опубликовать'); }
    setBusy(false);
  };

  return (
    <div>
      <div style={styles.hint}>
        Публикация новой версии требует от КАЖДОГО врача повторного принятия в личном кабинете
        (кабинет блокируется для добавления пациентов, пока врач не примет новую редакцию).
      </div>

      {!showNew ? (
        <button style={styles.btnPrimary} onClick={() => setShowNew(true)}>+ Опубликовать новую версию</button>
      ) : (
        <div style={styles.formCard}>
          <div style={styles.formLabel}>Заголовок</div>
          <input style={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Оферта для врачей v2" />
          <div style={styles.formLabel}>Текст оферты</div>
          <textarea style={styles.textarea} value={content} onChange={e => setContent(e.target.value)} rows={14} />
          {err && <div style={styles.err}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button style={styles.btnGhost} onClick={() => { setShowNew(false); setErr(''); }}>Отмена</button>
            <button style={styles.btnPrimary} onClick={publish} disabled={busy}>{busy ? 'Публикация…' : 'Опубликовать'}</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        {versions.map(v => (
          <div key={v.id} style={styles.versionCard}>
            <div style={styles.versionHeader}>
              <span style={styles.versionTitle}>{v.title}</span>
              <span style={styles.versionDate}>опубликовано {fmtDate(v.published_at)}</span>
            </div>
            <div style={styles.versionContent}>{v.content}</div>
          </div>
        ))}
        {versions.length === 0 && <div style={styles.empty}>Версий оферты пока нет</div>}
      </div>
    </div>
  );
}

// ── Вкладка «Договоры» ──
function ContractsTab({ headers }) {
  const [rows, setRows] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [targetKind, setTargetKind] = useState('clinic'); // clinic | network
  const [targetId, setTargetId] = useState('');
  const [number, setNumber] = useState('');
  const [cdate, setCdate] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/admin/clinic-contracts`, { headers }).then(r => setRows(r.data));
  }, [headers]);

  useEffect(() => {
    load();
    axios.get(`${API}/admin/clinics`, { headers, params: { limit: 2000 } }).then(r => setClinics(r.data.items));
    axios.get(`${API}/admin/networks`, { headers, params: { limit: 2000 } }).then(r => setNetworks(r.data.items));
    // eslint-disable-next-line
  }, []);

  const create = async () => {
    if (!targetId) { setErr('Выберите клинику или сеть'); return; }
    if (!number.trim() || !cdate) { setErr('Заполните номер и дату договора'); return; }
    setBusy(true); setErr('');
    try {
      const params = {
        [targetKind === 'clinic' ? 'clinic_id' : 'network_id']: targetId,
        contract_number: number.trim(), contract_date: cdate, notes: notes.trim() || undefined,
      };
      const fd = new FormData();
      if (file) fd.append('file', file);
      await axios.post(`${API}/admin/clinic-contracts`, fd, { headers, params });
      setShowNew(false); setTargetId(''); setNumber(''); setCdate(''); setNotes(''); setFile(null);
      load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось добавить договор'); }
    setBusy(false);
  };

  const downloadFile = (id) => {
    axios.get(`${API}/admin/clinic-contracts/${id}/file`, { headers, responseType: 'blob' })
      .then(r => {
        const url = window.URL.createObjectURL(r.data);
        window.open(url, '_blank');
      }).catch(() => {});
  };

  return (
    <div>
      <div style={styles.hint}>
        Реальные двусторонние договоры с клиниками/сетями (номер, дата, скан). Номер/дата
        подставляются в PDF счёта конкретной цели. Новая клиника/сеть создаётся скрытой от
        врачей — договор нужно завести здесь до того, как показать её (см. страницу
        клиники/сети → «Показать»).
      </div>

      {!showNew ? (
        <button style={styles.btnPrimary} onClick={() => setShowNew(true)}>+ Добавить договор</button>
      ) : (
        <div style={styles.formCard}>
          <div style={styles.row3}>
            <div>
              <div style={styles.formLabel}>Тип цели</div>
              <select style={styles.input} value={targetKind} onChange={e => { setTargetKind(e.target.value); setTargetId(''); }}>
                <option value="clinic">Клиника</option>
                <option value="network">Сеть</option>
              </select>
            </div>
            <div>
              <div style={styles.formLabel}>{targetKind === 'clinic' ? 'Клиника' : 'Сеть'}</div>
              <select style={styles.input} value={targetId} onChange={e => setTargetId(e.target.value)}>
                <option value="">— выбрать —</option>
                {(targetKind === 'clinic' ? clinics : networks).map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={styles.row3}>
            <div>
              <div style={styles.formLabel}>Номер договора</div>
              <input style={styles.input} value={number} onChange={e => setNumber(e.target.value)} placeholder="0001/260101" />
            </div>
            <div>
              <div style={styles.formLabel}>Дата договора</div>
              <input type="date" style={styles.input} value={cdate} onChange={e => setCdate(e.target.value)} />
            </div>
          </div>
          <div style={styles.formLabel}>Скан договора (опционально)</div>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0] || null)} style={{ marginBottom: 10 }} />
          <div style={styles.formLabel}>Примечание</div>
          <input style={styles.input} value={notes} onChange={e => setNotes(e.target.value)} />
          {err && <div style={styles.err}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button style={styles.btnGhost} onClick={() => { setShowNew(false); setErr(''); }}>Отмена</button>
            <button style={styles.btnPrimary} onClick={create} disabled={busy}>{busy ? 'Сохранение…' : 'Добавить'}</button>
          </div>
        </div>
      )}

      <div style={styles.table}>
        <div style={styles.thead}>
          <div style={styles.th}>Цель</div>
          <div style={styles.th}>Номер</div>
          <div style={styles.th}>Дата</div>
          <div style={styles.th}>Примечание</div>
          <div style={styles.th}>Файл</div>
        </div>
        {rows.map(r => (
          <div key={r.id} style={styles.trow}>
            <div style={styles.td}>{r.target_name}</div>
            <div style={styles.td}>{r.contract_number}</div>
            <div style={styles.td}>{r.contract_date}</div>
            <div style={styles.td}>{r.notes || '—'}</div>
            <div style={styles.td}>
              {r.has_file ? <span style={styles.link} onClick={() => downloadFile(r.id)}>Скачать</span> : '—'}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div style={styles.empty}>Договоров пока нет</div>}
      </div>
    </div>
  );
}

const styles = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #334155', marginBottom: 20 },
  tab: { padding: '10px 18px', color: '#94a3b8', fontSize: 14, fontWeight: 600, cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#2EC4B6', borderBottom: '2px solid #2EC4B6' },
  hint: { fontSize: 12, color: '#64748b', background: '#1e293b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, borderLeft: '3px solid #334155' },
  btnPrimary: { background: '#2EC4B6', color: '#0f172a', border: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', padding: '10px 18px', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  formCard: { background: '#1e293b', borderRadius: 12, padding: 20, marginTop: 12, maxWidth: 640 },
  formLabel: { fontSize: 12, color: '#94a3b8', marginBottom: 6, marginTop: 10 },
  input: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: '8px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: '10px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  err: { color: '#f87171', fontSize: 12, marginTop: 10 },
  empty: { color: '#64748b', fontSize: 13, padding: '20px 0' },
  versionCard: { background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 12 },
  versionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 6 },
  versionTitle: { color: '#e2e8f0', fontSize: 14, fontWeight: 700 },
  versionDate: { color: '#64748b', fontSize: 12 },
  versionContent: { color: '#94a3b8', fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 160, overflowY: 'auto' },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden', marginTop: 20 },
  thead: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr', padding: '10px 16px', borderBottom: '1px solid #334155', fontSize: 11, color: '#64748b', textTransform: 'uppercase' },
  th: {},
  trow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr', padding: '12px 16px', borderBottom: '1px solid #263548', fontSize: 13, color: '#e2e8f0', alignItems: 'center' },
  td: {},
  link: { color: '#2EC4B6', cursor: 'pointer', fontWeight: 600 },
};
